import { computed } from 'mobx';

import { concatPath } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel, isWretchError, wretch } from '@feathr/rachis';

export type TMetaIntegrationStatus = 'not_connected' | 'configuring' | 'connected';

/** OAuth config returned by the backend */
export interface IMetaOAuthConfig {
  app_id: string;
  api_version: string;
  redirect_uri: string;
  config_id: string;
}

/** Individual Facebook or Instagram page */
export interface IMetaPage {
  id: string;
  page_id: string;
  name: string;
  category?: string;
  fan_count?: number;
  fb_business_id?: string;
  fb_business_name?: string;
  instagram_account_id?: string;
  instagram_username?: string;
}

/** User who initiated the Meta connection */
export interface IMetaConnectedBy {
  id: string;
  email: string;
  name: string;
}

/** First connection record for a given Feathr user. */
export interface IMetaLastUserConnection {
  id: string;
  /** Date the user first initiated the integration. Does not update on reconnection. */
  connected_at: string;
  is_token_expired: boolean;
  connected_by: IMetaConnectedBy;
}

/** Attributes for Meta integration model */
export interface IMetaIntegrationAttributes extends IBaseAttributes {
  /** Link to Feathr account (unique - only one MetaAccount per Feathr account) */
  account_id: string;
  status: TMetaIntegrationStatus;
  /** Returns True if setup is complete and BM token is valid. */
  is_healthy: boolean;
  type: 'MetaAccount';
  oauth_config?: IMetaOAuthConfig;
  last_user_connection?: IMetaLastUserConnection;
}

/** Response from POST /integrations/meta/disconnect */
export interface IMetaDisconnectResponse {
  status: 'disconnected';
  connections_deactivated: number;
}

export class MetaIntegration extends DisplayModel<IMetaIntegrationAttributes> {
  public readonly className = 'MetaIntegration';

  public get constraints(): TConstraints<IMetaIntegrationAttributes> {
    return {
      status: {
        presence: true,
        inclusion: ['not_connected', 'configuring', 'connected'],
      },
    };
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath('integrations/meta', pathSuffix);
  }

  public get name(): string {
    return 'Meta Integration';
  }

  @computed
  public get isConnected(): boolean {
    return this.get('status') === 'connected';
  }

  @computed
  public get isConfiguring(): boolean {
    return this.get('status') === 'configuring';
  }

  @computed
  public get oauthConfig(): IMetaOAuthConfig | undefined {
    return this.get('oauth_config');
  }

  @computed
  public get lastUserConnection(): IMetaLastUserConnection | undefined {
    return this.get('last_user_connection');
  }

  /** Date the user first connected. Does not update on reconnection. */
  @computed
  public get connectedAt(): string | undefined {
    return this.lastUserConnection?.connected_at;
  }

  @computed
  public get connectedBy(): IMetaConnectedBy | undefined {
    return this.lastUserConnection?.connected_by;
  }

  @computed
  public get isTokenExpired(): boolean {
    return this.lastUserConnection?.is_token_expired ?? false;
  }

  @computed
  public get isHealthy(): boolean {
    return this.get('is_healthy') ?? false;
  }

  /**
   * Get Facebook pages connected to this integration.
   * GET /integrations/meta/pages
   */
  public async getPages(): Promise<IMetaPage[]> {
    if (!this.isConnected && !this.isConfiguring) {
      return [];
    }

    this.assertCollection(this.collection);

    const url = `${this.collection.getHostname()}integrations/meta/pages`;
    const response = await wretch<IMetaPage[] & Record<string, unknown>>(url, {
      headers: this.collection.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  /**
   * Disconnect the Meta integration.
   * POST /integrations/meta/disconnect
   */
  public async disconnect(): Promise<IMetaDisconnectResponse> {
    this.assertCollection(this.collection);

    const url = `${this.collection.getHostname()}integrations/meta/disconnect`;
    const response = await wretch<IMetaDisconnectResponse & Record<string, unknown>>(url, {
      headers: this.collection.getHeaders(),
      method: 'POST',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    this.set({
      status: 'not_connected',
      last_user_connection: undefined,
      is_healthy: false,
    } as Partial<IMetaIntegrationAttributes>);

    return response.data;
  }

  /**
   * Build the OAuth URL to initiate Meta connection.
   * Requires oauth_config to be present.
   */
  public getOAuthUrl(): string | null {
    const config = this.oauthConfig;
    if (!config) {
      return null;
    }

    const { app_id: appId, api_version: apiVersion, redirect_uri: redirectUri } = config;
    const oauthUrl = new URL(`https://www.facebook.com/${apiVersion}/dialog/oauth`);
    oauthUrl.searchParams.set('client_id', appId);
    oauthUrl.searchParams.set('redirect_uri', redirectUri);
    oauthUrl.searchParams.set('config_id', config.config_id);
    oauthUrl.searchParams.set('response_type', 'code');

    return oauthUrl.toString();
  }
}

export class MetaIntegrations extends Collection<MetaIntegration> {
  public getModel(attributes: IMetaIntegrationAttributes): MetaIntegration {
    return new MetaIntegration(attributes);
  }

  public getClassName(): string {
    return 'meta_integration';
  }

  public override url(): string {
    return `${this.getHostname()}integrations/meta/account`;
  }

  /**
   * Fetch the Meta integration status.
   * Unlike other collections, Meta has a single integration per account,
   * so we fetch from /account endpoint directly.
   */
  public async fetchIntegration(): Promise<MetaIntegration> {
    const response = await wretch<IMetaIntegrationAttributes>(this.url(), {
      headers: this.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return this.processJSONResponse(response);
  }
}
